import Seo from "@/components/Seo";
import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, Check } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { FEATURES, SERVICES, STATS, IMAGES, CONTACT, PRODUCT_CATEGORIES } from "@/lib/data";

const EASE = [0.76, 0, 0.24, 1];

// Disable scroll-linked parallax on touch / small screens.
// iOS Safari's collapsing address bar resizes the viewport on scroll, which
// makes scroll-driven transforms jitter ("twitching"). Static there = smooth.
function useDisableParallax() {
  const [disabled, setDisabled] = useState(true);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px), (pointer: coarse)");
    const update = () => setDisabled(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return disabled;
}

function Hero() {
  const ref = useRef(null);
  const disableParallax = useDisableParallax();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const overlayO = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden" data-testid="hero">
      <motion.div className="absolute inset-0 z-0" style={disableParallax ? undefined : { y: imgY, scale: imgScale }}>
        <img src={IMAGES.heroBuilding} alt="Illuminated architectural facade at night" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div className="absolute inset-0 z-10 bg-[#0A0A0A]" style={disableParallax ? { opacity: 0.6 } : { opacity: overlayO }} />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/20 to-[#0A0A0A]/60" />

      <motion.div style={disableParallax ? undefined : { y: textY }} className="relative z-20 max-w-[1400px] w-full mx-auto px-5 md:px-10 pt-28 md:pt-32 pb-16 md:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="text-xs uppercase tracking-[0.28em] text-gold/90 font-semibold mb-6 flex items-center gap-3"
        >
          <span className="inline-block w-8 h-px bg-gold" /> Serving Canada since {CONTACT.since}
        </motion.p>

        <p className="font-display uppercase font-medium text-white text-[13.5vw] md:text-[10.5vw] lg:text-[9vw] leading-[0.85] tracking-tighter">
          <MaskedLines lines={["We light", "the way for", "Canada."]} once={false} delay={0.35} />
        </p>

        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
          className="mt-6 max-w-2xl text-white/85 text-lg md:text-2xl font-medium leading-snug"
        >
          Wholesale Lighting &amp; Electrical Supplier in Toronto
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.8, ease: EASE }}
          className="mt-7"
        >
          <span
            data-testid="retail-badge"
            className="inline-flex items-center gap-2.5 border border-gold/40 bg-gold/[0.06] text-gold text-[11px] md:text-xs uppercase tracking-[0.16em] font-semibold px-4 py-2"
          >
            <Check className="w-3.5 h-3.5" strokeWidth={3} />
            Open to the public
            <span className="w-1 h-1 rounded-full bg-gold/60" />
            No minimum order
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <p className="max-w-md text-lg text-white/70 leading-relaxed">
            A family-operated wholesale &amp; retail supplier of lighting and electrical products — open to
            contractors, businesses and the public alike. Competitive pricing, licensed installation, and no minimum
            order — ever.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/contact"
              data-testid="hero-quote-cta"
              className="inline-flex items-center gap-2 bg-gold text-white text-sm uppercase tracking-[0.14em] font-semibold px-7 py-4 whitespace-nowrap shrink-0 hover:bg-[#b00000] transition-colors duration-300"
            >
              Request a Quote <ArrowUpRight className="w-4 h-4 shrink-0" />
            </Link>
            <Link
              to="/services"
              className="link-sweep inline-flex items-center gap-2 text-sm uppercase tracking-[0.14em] text-white/80 hover:text-white transition-colors duration-300"
            >
              Our Services
            </Link>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute z-20 right-5 md:right-10 bottom-16 md:bottom-24 hidden sm:flex items-center gap-2 text-white/40 text-xs uppercase tracking-[0.2em]"
      >
        <ArrowDown className="w-4 h-4 animate-bounce" /> Scroll
      </motion.div>
    </section>
  );
}

function Stats() {
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28" data-testid="stats">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6 border-t border-white/10 pt-12">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08}>
            <div>
              <p className="font-display text-gold text-5xl md:text-6xl leading-none tracking-tight">{s.value}</p>
              <p className="mt-3 text-sm text-white/50 uppercase tracking-[0.12em]">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function WhyUs() {
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24" data-testid="why-us">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-14">
        <div className="lg:col-span-8">
          <p className="text-xs uppercase tracking-[0.2em] text-gold/80 font-semibold mb-5">Why choose us</p>
          <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight text-balance">
            <MaskedLines lines={["Trusted by trades,", "built for everyone."]} />
          </h2>
        </div>
        <div className="lg:col-span-4">
          <Reveal delay={0.2}>
            <p className="text-white/60 leading-relaxed">
              From single-fixture replacements to full commercial developments, every customer receives the same
              professional service and honest pricing.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {FEATURES.map((f, i) => (
          <Reveal key={f.title} delay={(i % 3) * 0.08} className="group">
            <div className="h-full bg-[#0A0A0A] p-8 md:p-10 hover:bg-[#121212] transition-colors duration-400">
              <span className="font-display text-white/25 text-lg group-hover:text-gold transition-colors duration-400">
                0{i + 1}
              </span>
              <h3 className="mt-6 font-display uppercase text-xl md:text-2xl tracking-tight leading-tight">{f.title}</h3>
              <p className="mt-4 text-white/55 leading-relaxed text-sm">{f.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ManifestoNoMinimum() {
  const ref = useRef(null);
  const disableParallax = useDisableParallax();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section ref={ref} className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-32" data-testid="no-minimum">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <span className="font-display text-gold text-7xl md:text-8xl leading-none">02</span>
          <h2 className="mt-6 font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight text-balance">
            <MaskedLines lines={["No minimum", "order. Period."]} />
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-8 text-lg text-white/70 leading-relaxed max-w-lg">
              Replacing a single fixture, stocking a renovation, or specifying a large commercial development — every
              customer gets the same competitive pricing and expert support.
            </p>
            <p className="mt-6 font-display uppercase text-2xl text-white tracking-tight">
              One fixture or one thousand — <span className="text-gold">we&apos;re ready.</span>
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="overflow-hidden aspect-[4/5]">
            <motion.img
              style={disableParallax ? { scale: 1.08 } : { y, scale: 1.15 }}
              src={IMAGES.electrician2}
              alt="Lighting fixtures being installed"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesPeek() {
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24" data-testid="services-peek">
      <div className="flex items-end justify-between mb-12 gap-6">
        <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight">
          <MaskedLines lines={["What we do"]} />
        </h2>
        <Link
          to="/services"
          className="link-sweep hidden sm:inline-flex items-center gap-2 text-sm uppercase tracking-[0.14em] text-white/70 hover:text-white"
        >
          All services <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        {SERVICES.map((s, i) => (
          <Reveal key={s.num} delay={i * 0.1}>
            <Link to="/services" className="group block">
              <div className="overflow-hidden aspect-[3/4] mb-6">
                <img
                  src={s.image}
                  alt={s.title}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 scale-105 group-hover:scale-100 transition-[filter,transform] duration-700 ease-out"
                />
              </div>
              <div className="flex items-baseline gap-4">
                <span className="font-display text-gold text-sm">{s.num}</span>
                <h3 className="font-display uppercase text-2xl tracking-tight group-hover:text-gold transition-colors duration-300">
                  {s.title}
                </h3>
              </div>
              <p className="mt-3 text-white/55 text-sm leading-relaxed">{s.body}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function WholesaleForContractors() {
  const segments = [
    "Electrical contractors",
    "Electrical distributors",
    "Commercial & industrial businesses",
    "Municipalities & institutions",
    "Retailers & property managers",
    "Renovators & homeowners",
  ];
  const products = [
    "Energy-efficient LED lighting fixtures",
    "Fluorescent & halogen lamps",
    "Specialty & projector lamps",
    "LED rope & accent lighting",
    "Electrical supplies",
    "Custom product sourcing",
  ];
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24" data-testid="wholesale-contractors">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.2em] text-gold/80 font-semibold mb-5">Wholesale &amp; retail</p>
          <h2 className="font-display uppercase text-4xl md:text-5xl leading-[0.95] tracking-tight">
            Wholesale<br />electrical &amp;<br />lighting fixtures.
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-8 text-white/60 leading-relaxed text-justify">
              Saver Plus Lighting Corp. is a wholesale and retail electrical and lighting supplier serving contractors,
              businesses, retailers and the public throughout the Greater Toronto Area and across Canada. From a single
              bulb to outfitting an entire facility, you get competitive pricing, expert advice, and no minimum
              order — plus licensed installation when you need it.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 grid grid-cols-1 sm:grid-cols-2 gap-10">
          <div>
            <h3 className="text-sm uppercase tracking-[0.14em] text-white mb-5">Who we supply</h3>
            <ul className="space-y-3">
              {segments.map((s, i) => (
                <Reveal key={s} delay={i * 0.05}>
                  <li className="flex items-center gap-3 text-white/60">
                    <span className="w-1.5 h-1.5 bg-gold shrink-0" /> {s}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-sm uppercase tracking-[0.14em] text-white mb-5">What we stock &amp; source</h3>
            <ul className="space-y-3">
              {products.map((p, i) => (
                <Reveal key={p} delay={i * 0.05}>
                  <li className="flex items-center gap-3 text-white/60">
                    <span className="w-1.5 h-1.5 bg-gold shrink-0" /> {p}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatWeSupply() {
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24" data-testid="what-we-supply">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10 md:mb-14">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold/80 font-semibold mb-5">What we supply</p>
          <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight">
            <MaskedLines lines={["A catalogue", "without limits."]} once={false} />
          </h2>
        </div>
        <Link
          to="/products"
          data-testid="home-products-cta"
          className="link-sweep inline-flex items-center gap-2 text-sm uppercase tracking-[0.14em] text-white/70 hover:text-white shrink-0"
        >
          All products <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {PRODUCT_CATEGORIES.map((c, i) => (
          <Reveal key={c.num} delay={(i % 4) * 0.06}>
            <Link
              to="/products"
              className="group flex items-center justify-between gap-3 h-full bg-[#0A0A0A] p-6 md:p-7 hover:bg-[#121212] transition-colors duration-400"
            >
              <h3 className="font-display uppercase text-sm md:text-base tracking-tight leading-tight text-white/85 group-hover:text-gold transition-colors duration-300">
                {c.title}
              </h3>
              <ArrowUpRight className="w-4 h-4 text-white/25 group-hover:text-gold group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300 shrink-0" />
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div>
      <Seo
        title="Saver Plus Lighting Corp (SPLC) — Lighting & Electrical Supplier | Toronto & GTA"
        description="Saver Plus Lighting Corp (SPLC) is a Toronto lighting and electrical supplier serving the GTA — wholesale and retail, open to the public with no minimum order. LED lighting, commercial lighting, bulbs, tubes, emergency lighting and electrical supplies. Family-operated since 1980. Call 416-767-0960."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Store",
          "@id": "https://saverpluslighting.com/#business",
          name: "Saver Plus Lighting Corp.",
          alternateName: ["SPLC", "Saver Plus Lighting Corporation", "Saver Plus Lighting Corp", "Saver Plus Lighting"],
          description:
            "Family-operated Toronto lighting and electrical supplier serving the GTA — wholesale and retail, open to the public with no minimum order. Lighting, LED, bulbs, tubes, electrical supplies, emergency & exit lighting and fire alarm products.",
          url: "https://saverpluslighting.com/",
          telephone: "+1-416-767-0960",
          email: "info@saverpluslighting.com",
          image: "https://saverpluslighting.com/assets/products-og.jpg",
          logo: "https://saverpluslighting.com/assets/saver-plus-logo.png",
          foundingDate: "1980",
          priceRange: "$$",
          address: {
            "@type": "PostalAddress",
            streetAddress: "17 Henrietta St",
            addressLocality: "Toronto",
            addressRegion: "ON",
            postalCode: "M6N 1S4",
            addressCountry: "CA",
          },
          areaServed: [
            { "@type": "City", name: "Toronto" },
            { "@type": "AdministrativeArea", name: "Greater Toronto Area" },
            { "@type": "City", name: "Mississauga" },
            { "@type": "City", name: "Brampton" },
            { "@type": "City", name: "Vaughan" },
            { "@type": "City", name: "Markham" },
            { "@type": "AdministrativeArea", name: "Ontario" },
            { "@type": "Country", name: "Canada" },
          ],
          openingHoursSpecification: {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "07:00",
            closes: "19:00",
          },
          knowsAbout: [
            "wholesale lighting",
            "retail lighting",
            "electrical supplies",
            "LED lighting fixtures",
            "commercial lighting",
            "emergency and exit lighting",
            "fire alarm products",
            "lighting for contractors",
            "professional lighting installation",
          ],
        }}
      />
      <Hero />
      <Marquee text="No Minimum Order" />
      <Stats />
      <WhyUs />
      <WholesaleForContractors />
      <ManifestoNoMinimum />
      <Marquee text="Since 1980" separator="·" />
      <ServicesPeek />
      <WhatWeSupply />
    </div>
  );
}
